/**
 * Print-Only Client Profile Page for PDF Generation
 * Renders comprehensive client and business profile data for PDF export
 *
 * URL: /print/client-profile/:businessId
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../../print.css';

const PrintClientProfile = () => {
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get token from URL parameter (for PDF generation) or localStorage (for direct viewing)
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        const token = urlToken || localStorage.getItem('token') || sessionStorage.getItem('token');

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Business-Id': businessId
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/business/profile`, { headers });

        if (!response.ok) {
          throw new Error('Failed to fetch profile data');
        }

        const profileData = await response.json();
        setData(profileData.data.profile);
        setLoading(false);
      } catch (err) {
        console.error('[PrintClientProfile] Error loading client profile:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId]);

  useEffect(() => {
    if (!loading && data) {
      document.body.setAttribute('data-pdf-ready', 'true');

      // Set document title to business name
      const businessName = data?.business_name || 'Business';
      document.title = `${businessName} - Client Profile Report`;
    }
  }, [loading, data]);

  if (loading) {
    return (
      <div className="print-container" data-pdf-loading="true">
        <div className="loading">Loading client profile data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container" data-pdf-error="true">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const profile = data || {};
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const parseJSON = (field) => {
    try {
      return typeof field === 'string' ? JSON.parse(field) : field;
    } catch {
      return field || [];
    }
  };

  const owners = parseJSON(profile.owners) || [];
  const dependents = parseJSON(profile.dependents_info) || [];
  const customAdvisors = parseJSON(profile.custom_advisors) || [];
  const keyMotivations = parseJSON(profile.key_motivations) || [];
  const keyMotivationsOther = parseJSON(profile.key_motivations_other) || [];
  const dealBreakers = parseJSON(profile.deal_breakers) || [];
  const dealBreakersOther = parseJSON(profile.deal_breakers_other) || [];

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 ? age : '';
  };

  const formatFullName = (prefix, firstName, lastName, suffix) => {
    const parts = [];
    if (prefix) parts.push(prefix);
    if (firstName) parts.push(firstName);
    if (lastName) parts.push(lastName);
    if (suffix) parts.push(suffix);
    return parts.join(' ');
  };

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div className="cover-header">
          <h1>Client Profile Report</h1>
          <div className="report-type">Comprehensive Business & Personal Information</div>
        </div>

        <div className="cover-business-info">
          <h2>{profile.business_name || 'Business Name Not Set'}</h2>
          {profile.industry && <p className="industry">{profile.industry}</p>}
        </div>

        <div className="cover-footer">
          <p>Generated {today}</p>
          <p className="confidential">Confidential Client Profile Report</p>
        </div>
      </div>

      {/* Client Personal Information, Spouse/Partner, and Family & Dependents - Combined Page */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Client Personal Information</h1>

        {/* Client and Spouse Side by Side */}
        <div style={{ display: 'grid', gridTemplateColumns: (profile.spouse_first_name || profile.spouse_last_name) ? '1fr 1fr' : '1fr', gap: '0.2in', marginBottom: '0.15in' }} className="no-break-inside">
          {/* Client Information Column */}
          <div>
            <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.08in', marginTop: '0' }}>Client</h3>
            <table className="info-table">
              <tbody>
                {profile.client_first_name && (
                  <tr>
                    <th>Full Name</th>
                    <td>{formatFullName(profile.client_prefix, profile.client_first_name, profile.client_last_name, profile.client_suffix)}</td>
                  </tr>
                )}
                {profile.client_email && (
                  <tr>
                    <th>Email</th>
                    <td>{profile.client_email}</td>
                  </tr>
                )}
                {profile.client_phone && (
                  <tr>
                    <th>Phone</th>
                    <td>{profile.client_phone}</td>
                  </tr>
                )}
                {profile.client_date_of_birth && (
                  <tr>
                    <th>Date of Birth</th>
                    <td>
                      {new Date(profile.client_date_of_birth).toLocaleDateString('en-US')}
                      {calculateAge(profile.client_date_of_birth) && ` (Age: ${calculateAge(profile.client_date_of_birth)})`}
                    </td>
                  </tr>
                )}
                {profile.client_sex && (
                  <tr>
                    <th>Sex</th>
                    <td>{profile.client_sex}</td>
                  </tr>
                )}
                {profile.client_address && (
                  <tr>
                    <th>Address</th>
                    <td>
                      {profile.client_address}
                      {(profile.client_city || profile.client_state || profile.client_zip) && (
                        <>
                          <br />
                          {profile.client_city && `${profile.client_city}, `}
                          {profile.client_state} {profile.client_zip}
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {profile.client_notes && (
              <div className="disclosure-box" style={{ marginTop: '0.1in' }}>
                <h3>Notes</h3>
                <p>{profile.client_notes}</p>
              </div>
            )}
          </div>

          {/* Spouse/Partner Information Column */}
          {(profile.spouse_first_name || profile.spouse_last_name || profile.spouse_name) && (
            <div>
              <h3 style={{ fontSize: '10pt', color: '#1e3a8a', marginBottom: '0.08in', marginTop: '0' }}>Spouse/Partner</h3>
              <table className="info-table">
                <tbody>
                  {(profile.spouse_first_name || profile.spouse_last_name) && (
                    <tr>
                      <th>Full Name</th>
                      <td>{formatFullName(profile.spouse_prefix, profile.spouse_first_name, profile.spouse_last_name, profile.spouse_suffix)}</td>
                    </tr>
                  )}
                  {profile.spouse_email && (
                    <tr>
                      <th>Email</th>
                      <td>{profile.spouse_email}</td>
                    </tr>
                  )}
                  {profile.spouse_phone && (
                    <tr>
                      <th>Phone</th>
                      <td>{profile.spouse_phone}</td>
                    </tr>
                  )}
                  {profile.spouse_date_of_birth && (
                    <tr>
                      <th>Date of Birth</th>
                      <td>
                        {new Date(profile.spouse_date_of_birth).toLocaleDateString('en-US')}
                        {calculateAge(profile.spouse_date_of_birth) && ` (Age: ${calculateAge(profile.spouse_date_of_birth)})`}
                      </td>
                    </tr>
                  )}
                  {profile.spouse_sex && (
                    <tr>
                      <th>Sex</th>
                      <td>{profile.spouse_sex}</td>
                    </tr>
                  )}
                  <tr>
                    <th>Involved in Business</th>
                    <td>{profile.spouse_involved_in_business ? 'Yes' : 'No'}</td>
                  </tr>
                </tbody>
              </table>
              {profile.spouse_notes && (
                <div className="disclosure-box" style={{ marginTop: '0.1in' }}>
                  <h3>Notes</h3>
                  <p>{profile.spouse_notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Family & Dependents - Same Page */}
        {dependents.length > 0 && (
          <>
            <h2 className="section-title" style={{ marginTop: '0.15in' }}>Family & Dependents</h2>

            <p style={{ marginBottom: '0.08in', fontSize: '8pt' }}>
              <strong>Number of Dependents:</strong> {profile.num_dependents || dependents.length}
            </p>

            <table className="metrics-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Relationship</th>
                  <th>Date of Birth</th>
                  <th>Age</th>
                  <th>Sex</th>
                </tr>
              </thead>
              <tbody>
                {dependents.map((dependent, index) => (
                  <tr key={index}>
                    <td>{dependent.name || '-'}</td>
                    <td>{dependent.relationship || '-'}</td>
                    <td>{dependent.date_of_birth ? new Date(dependent.date_of_birth).toLocaleDateString('en-US') : '-'}</td>
                    <td>{calculateAge(dependent.date_of_birth) || '-'}</td>
                    <td>{dependent.sex || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {profile.dependents_notes && (
              <div className="disclosure-box" style={{ marginTop: '0.1in' }}>
                <h3>Notes</h3>
                <p>{profile.dependents_notes}</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Business Information Page */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Business Information</h1>

        <table className="info-table">
          <tbody>
            {profile.business_name && (
              <tr>
                <th>Business Name</th>
                <td>{profile.business_name}</td>
              </tr>
            )}
            {profile.industry && (
              <tr>
                <th>Industry</th>
                <td>{profile.industry}</td>
              </tr>
            )}
            {profile.employees && (
              <tr>
                <th>Number of Employees</th>
                <td>{profile.employees}</td>
              </tr>
            )}
            {profile.year_founded && (
              <tr>
                <th>Year Founded</th>
                <td>{profile.year_founded} ({new Date().getFullYear() - parseInt(profile.year_founded)} years in business)</td>
              </tr>
            )}
            {profile.primary_location && (
              <tr>
                <th>Primary Location</th>
                <td>{profile.primary_location}</td>
              </tr>
            )}
            {profile.primary_market && (
              <tr>
                <th>Primary Market</th>
                <td>{profile.primary_market}</td>
              </tr>
            )}
            {profile.registration_type && (
              <tr>
                <th>Registration Type</th>
                <td>{profile.registration_type}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Business Ownership - Same Page */}
        {owners.length > 0 && (
          <>
            <h2 className="section-title" style={{ marginTop: '0.15in' }}>Business Ownership</h2>
            <table className="metrics-table">
              <thead>
                <tr>
                  <th>Owner Name</th>
                  <th>Ownership %</th>
                  <th>Email</th>
                  <th>Phone</th>
                </tr>
              </thead>
              <tbody>
                {owners.map((owner, index) => (
                  <tr key={index}>
                    <td>{owner.name || '-'}</td>
                    <td>{owner.ownership_percentage ? `${owner.ownership_percentage}%` : '-'}</td>
                    <td>{owner.email || '-'}</td>
                    <td>{owner.phone || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {profile.business_notes && (
          <div className="disclosure-box" style={{ marginTop: '0.1in' }}>
            <h3>Notes</h3>
            <p>{profile.business_notes}</p>
          </div>
        )}
      </div>

      {/* Exit Planning Goals - New Page */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Exit Planning Goals</h1>

        {/* Exit Planning Data*/}
          <div>
            <table className="info-table">
              <tbody>
                {profile.exit_horizon && (
                  <tr>
                    <th>Exit Horizon</th>
                    <td>{profile.exit_horizon}</td>
                  </tr>
                )}
                {profile.preferred_exit_type && (
                  <tr>
                    <th>Preferred Exit Type</th>
                    <td>{profile.preferred_exit_type}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Key Motivations */}
            {(keyMotivations.length > 0 || keyMotivationsOther.length > 0) && (
              <>
                <h3 style={{ fontSize: '11pt', color: '#1e3a8a', marginTop: '0.2in', marginBottom: '0.05in' }}>Key Motivations</h3>
                <ul style={{ listStyle: 'disc', marginLeft: '0.2in', lineHeight: '1.4', fontSize: '9pt', marginTop: '0' }}>
                  {keyMotivations.map((motivation, index) => (
                    <li key={index}>{motivation}</li>
                  ))}
                  {keyMotivationsOther.map((motivation, index) => (
                    <li key={`other-${index}`}>{motivation}</li>
                  ))}
                </ul>
              </>
            )}

            {/* Deal Breakers */}
            {(dealBreakers.length > 0 || dealBreakersOther.length > 0) && (
              <>
                <h3 style={{ fontSize: '11pt', color: '#1e3a8a', marginTop: '0.2in', marginBottom: '0.05in' }}>Deal Breakers</h3>
                <ul style={{ listStyle: 'disc', marginLeft: '0.2in', lineHeight: '1.4', fontSize: '9pt', marginTop: '0' }}>
                  {dealBreakers.map((breaker, index) => (
                    <li key={index}>{breaker}</li>
                  ))}
                  {dealBreakersOther.map((breaker, index) => (
                    <li key={`other-${index}`}>{breaker}</li>
                  ))}
                </ul>
              </>
            )}
          </div>

        {profile.exit_notes && (
          <div className="disclosure-box" style={{ marginTop: '0.1in' }}>
            <h3>Notes</h3>
            <p>{profile.exit_notes}</p>
          </div>
          )}
      </div>

      {/* Advisory Team - Condensed */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Advisory Team</h1>

        <table className="metrics-table" style={{ marginTop: '0.1in' }}>
          <thead>
            <tr>
              <th>Role</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
            </tr>
          </thead>
          <tbody>
            {[
              { key: 'attorney', label: 'Business Attorney', has: profile.has_attorney },
              { key: 'accountant', label: 'CPA / Accountant', has: profile.has_accountant },
              { key: 'financial_advisor', label: 'Financial Advisor', has: profile.has_financial_advisor },
              { key: 'exit_advisor', label: 'Exit / M&A Advisor', has: profile.has_exit_advisor },
              { key: 'insurance_agent', label: 'Insurance Agent', has: profile.has_insurance_agent },
              { key: 'banker', label: 'Banker / Lender', has: profile.has_banker },
              { key: 'estate_planner', label: 'Estate Planner', has: profile.has_estate_planner },
              { key: 'business_coach', label: 'Business Coach', has: profile.has_business_coach }
            ].filter(advisor => advisor.has).map((advisor, index) => (
              <tr key={advisor.key}>
                <td style={{ fontWeight: '600', color: '#1e3a8a' }}>{advisor.label}</td>
                <td>{profile[`${advisor.key}_name`] || '-'}</td>
                <td>{profile[`${advisor.key}_email`] || '-'}</td>
                <td>{profile[`${advisor.key}_phone`] || '-'}</td>
              </tr>
            ))}
            {customAdvisors.map((advisor, index) => (
              <tr key={`custom-${index}`}>
                <td style={{ fontWeight: '600', color: '#1e3a8a' }}>{advisor.title || 'Custom Advisor'}</td>
                <td>{advisor.name || '-'}</td>
                <td>{advisor.email || '-'}</td>
                <td>{advisor.phone || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {profile.advisory_notes && (
          <div className="disclosure-box" style={{ marginTop: '0.15in' }}>
            <h3>Notes</h3>
            <p>{profile.advisory_notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintClientProfile;
